import { inject, Injectable, Injector } from '@angular/core';
import { FormFieldModel, FormModel, FormService, FormValues, ValidateFormEvent, WidgetVisibilityService } from '@alfresco/adf-core';
import { EMPTY, Observable, Subject, throwError } from 'rxjs';
import { EbenFormOutcomeModel, EbenTaskDetail, FormFieldWarning, FormProcessor } from '@alf-nx-workspace/eben/interfaces';
import { EbenFacade } from '@alf-nx-workspace/eben/data-access';

import { catchError, distinctUntilChanged, tap } from 'rxjs/operators';
import { FormProcessorService, SnackbarService } from '@alf-nx-workspace/shared/utils';
import { ProcessInstance, ProcessInstanceVariable, ProcessService } from "@alfresco/adf-process-services";
import { ProcessError, TaskError } from "@alf-nx-workspace/shared/utils";

interface FormValueState {
  original: string;
  value: string;
}

@Injectable({ providedIn: 'root' })
export class EbenFormService {

  readonly processService = inject(ProcessService)
  readonly snackbarService = inject(SnackbarService);

  /**
   * information if an eben-widget changes
   */
  public readonly formFieldChangedSubject: Subject<FormFieldModel> = new Subject<FormFieldModel>();
  public readonly formFieldChanged$: Observable<FormFieldModel> = this.formFieldChangedSubject.asObservable();

  /**
   * information if an eben-widget receives focus
   */
  private readonly formFieldWidgetFocusInSubject: Subject<FormFieldModel> = new Subject<FormFieldModel>();
  public readonly formFieldWidgetFocusIn$: Observable<FormFieldModel> =
    this.formFieldWidgetFocusInSubject.asObservable();

  /**
   * IS-1519 handles external form outcome triggers
   */
  public readonly outcomeHandleTrigger$: Subject<EbenFormOutcomeModel> = new Subject<EbenFormOutcomeModel>();
  public readonly outcomeRefreshVisibility$: Subject<EbenFormOutcomeModel> = new Subject<EbenFormOutcomeModel>();

  public form: FormModel;
  private formValueSnapshot: FormValueState;
  private ebenFormProcessor: FormProcessor;

  constructor(
    public formService: FormService,
    public ebenFacade: EbenFacade,
    public injector: Injector,
    public formProcessorService: FormProcessorService
  ) {


    /**
     * updates the state with the correct error fields
     */
    this.formService.validateForm
      .pipe(
        distinctUntilChanged((prev, curr) => {
          return prev.errorsField.length === curr.errorsField.length;
        })
      )
      .subscribe((event: ValidateFormEvent) => {
        const errorFormFields: FormFieldModel[] = event.errorsField;
        const errorsField: FormFieldModel[] = errorFormFields.filter(
          (field: FormFieldModel) => field.fieldType !== 'ContainerRepresentation'
        );
        this.ebenFacade.setFormErrorFields(errorsField);
      });
  }

  public formLoaded(form: FormModel): void {
    this.form = form;

    if (form.name) {
      // This applies for the task form
      this.ebenFormProcessor = this.formProcessorService.getFormProcessor(form.name);
    } else {
      // This applies for a start form.
      // Start forms don't supply a name, therefore the processDefinitionKey is used to select the
      // appropriate FormProcessor
      this.ebenFormProcessor = this.formProcessorService.getFormProcessor(form.json['processDefinitionKey']);
    }

    if(!this.ebenFormProcessor) {
      this.ebenFormProcessor = this.formProcessorService.getBaseProcessor();
    }

    if (this.ebenFormProcessor) {
      this.ebenFormProcessor.onLoaded(form);
    }
    this.updateFormIsDirtyState(JSON.stringify(form.values), JSON.stringify(form.values));
    this.updateFormFieldWarnings(form);
    this.ebenFacade.setFormModel(form);
  }

  /**
   * Validates the given form and triggers the associated form processor's
   * validation handler if it exists.
   *
   * @param {FormModel} form - The form object to be validated.
   * @return {void} Does not return a value.
   */
  public validateForm(form: FormModel) {
    if (this.form) {
      if(this.ebenFormProcessor) {
        this.ebenFormProcessor.onValidated(form);
      }
    }
  }

  /**
   * this method is used to inform the processor of changes made to a form field value
   * it is called from the eben-widget components
   *
   * @param field
   */
  public onFieldValueChange(field: FormFieldModel): void {
    // update the visibility state if all fields and then
    // update the validation of the form
    this.updateVisibility(field.form);
    this.updateFormIsDirtyState(null, JSON.stringify(field.form.values));

    this.ebenFacade.setFormValues(field.id, field.form.values);
    if (this.ebenFormProcessor) {
      this.ebenFormProcessor.onFieldValueChange(field);
      this.updateFormFieldWarnings(field.form);
    }
  }

  /**
   * this method is used to get informed when the form-processor updates a form field
   * changes can be made to the form value or the attributes
   *
   * @param field
   */
  public onFormFieldChanged(field: FormFieldModel): void {
    this.ebenFacade.setFormValues(field.id, field.form.values);
    this.formFieldChangedSubject.next(field);
  }

  /**
   * triggers when a form-field-widget receives focus
   * it is called from the eben-widget components
   *
   * @param field
   */
  public onFormFieldWidgetFocusIn(field: FormFieldModel): void {
    if (this.ebenFormProcessor) {
      this.ebenFormProcessor.onFieldFocusIn(field);
    }
    this.updateVisibility(field.form);
  }

  /**
   * triggers when a form-field-widget looses focus
   * it is called from the eben-widget components
   *
   * @param field
   */
  public onFormFieldWidgetFocusOut(field: FormFieldModel): void {
    if (this.ebenFormProcessor) {
      this.ebenFormProcessor.onFieldFocusOut(field);
    }
    this.updateVisibility(field.form);
  }

  public startProcess(processDefinitionId: string, name: string, outcome?: string, startFormValues?: FormValues, variables?: ProcessInstanceVariable[]): Observable<ProcessInstance> {
    return this.processService.startProcess(processDefinitionId, name, outcome, startFormValues, variables)
      .pipe(
        catchError((error) => {
          return throwError(ProcessError.createFromError(error, 'START', processDefinitionId));
        })
      )
  }



  /**
   *
   * @param taskId
   * @param formValues
   */
  public saveForm(taskId: string, formValues: FormValues): Observable<FormValueState> {
    return this.formService.saveTaskForm(taskId, formValues).pipe(
      tap(() => {
        this.updateFormIsDirtyState(JSON.stringify(this.form.values), JSON.stringify(this.form.values))
        this.ebenFacade.saveForm(this.form);
        this.snackbarService.info('SNACKBAR.FORM_SAVED');
      }),
      catchError((error) => {
        return throwError(TaskError.createFromError(error, 'SAVE', this.form.taskId));
      })
    );
  }

  /**
   *
   * @param taskId
   * @param formValues
   * @param outcome
   */
  public completeTaskForm(taskId: string, formValues: FormValues, outcome?: string): Observable<void> {
    return this.formService.completeTaskForm(taskId, formValues, outcome).pipe(
      tap(() => {
        this.ebenFacade.setFormModelDirty(false);
        this.ebenFacade.completeTask();
      }),
      catchError((error) => {
        return throwError(TaskError.createFromError(error, 'COMPLETE', taskId));
      })
    );
  }

  /**
   *
   * @param outcome
   */
  public outcomeRefreshVisibility(outcome: EbenFormOutcomeModel) {
    this.outcomeRefreshVisibility$.next(outcome);
  }

  /**
   *
   * @param outcome
   */
  public outcomeHandleTrigger(outcome: EbenFormOutcomeModel) {
    this.outcomeHandleTrigger$.next(outcome);
  }

  /**
   * stores the form field warnings generated by the form processor to the state
   * to show global warnings in the form
   *
   * @private
   */
  private updateFormFieldWarnings(form: FormModel) {
    if (this.ebenFormProcessor) {
      const warnings: FormFieldWarning[] = this.ebenFormProcessor.getFormFieldWarnings(form);
      this.ebenFacade.setFormFieldWarnings(warnings);
    }
  }

  /**
   * for custom widgets the form visibility is not checked so we have to do it manually
   * logic implemented from FormFieldComponent::100
   */
  public updateVisibility(form) {
    const visibilityService: WidgetVisibilityService = this.injector.get(WidgetVisibilityService);
    if (visibilityService && form) {
      visibilityService.refreshVisibility(form);
      // if an invalid field is visible now we have to make sure that the form is invalid
      (form as FormModel).validateForm();
    }
  }

  private updateFormIsDirtyState(original: string, value: string) {
    this.formValueSnapshot = {
      original: original ? original : this.formValueSnapshot.original,
      value: value ? value : this.formValueSnapshot.value
    }
    const isDirty = this.formValueSnapshot.value != this.formValueSnapshot.original;
    this.ebenFacade.setFormModelDirty(isDirty);
  }
}
